import { Provider, useDispatch } from "react-redux";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import store from "./store";
import Layout from "./layout/Layout";
import HeaderComponent from "./components/Visitor/Header/HeaderComponent";
import Home from "./layout/Visitor/Home/Home";
import Step1Request from "./layout/Visitor/Request/Step1";
import Step2Request from "./layout/Visitor/Request/Step2";
import StatusPage from "./layout/Visitor/Status/Status";
import QRPage from "./layout/Visitor/QR/QR";
import GatePass from "./layout/Visitor/GatePass/GatePass";
import InstructionsPage from "./layout/Visitor/Instructions/Instructions";
import MyRequests from "./layout/Visitor/MyRequests/MyRequests";
import RequestDetails from "./layout/Visitor/RequestDetails/RequestDetails";
import ProfileToken from "./layout/Visitor/ProfileToken/ProfileToken";
import AdminDashboard from "./layout/Admin/Dashboard/Dashboard";
import ApprovalManagement from "./layout/Admin/ApprovalManagement/ApprovalManagement";
import SecurityMonitoringPage from "./layout/Admin/SecurityMonitoring/SecurityMonitoring";
import RestrictedManagement from "./layout/Admin/BlacklistManagement/BlacklistManagement";
import AdminActiveVisitors from "./layout/Admin/ActiveVisitors/ActiveVisitors";

import UserManagement from "./layout/Admin/UserManagement/UserManagement";
import AllUsers from "./layout/Admin/AllUsers/AllUsers";
import VisitorManagement from "./layout/Admin/VisitorManagement/VisitorManagement";
import PlacesManagement from "./layout/Admin/PlacesManagement/PlacesManagement";
import ProfileTokenManagement from "./layout/Admin/ProfileTokenManagement/ProfileTokenManagement";
import AccessTokenManagement from "./layout/Admin/AccessTokenManagement/AccessTokenManagement";
import Login from "./layout/Login/Login";
import ServerConfig from "./layout/ServerConfig/ServerConfig";

import ContactDashboard from "./layout/Contact_Person/Dashboard/Dashboard";
// import RequestsInbox from "./layout/Contact_Person/RequestsInbox/RequestsInbox";
import RequestReview from "./layout/Contact_Person/RequestReview/RequestReview";
import DeclinedRequests from "./layout/Contact_Person/RejectedRequests/RejectedRequests";
import SentToAdmin from "./layout/Contact_Person/SentToAdmin/SentToAdmin";
import VisitorHistory from "./layout/Contact_Person/VisitorHistory/VisitorHistory";
import ProfileSettings from "./layout/Contact_Person/ProfileSettings/ProfileSettings";
import ContactAllVisitors from "./layout/Contact_Person/AllVisitors/AllVisitors";
import ContactVisitRequests from "./layout/Contact_Person/VisitRequests/VisitRequests";
import CreateVisitRequest from "./layout/Contact_Person/VisitRequests/CreateVisitRequest";
import CreateVisitRequestDetails from "./layout/Contact_Person/VisitRequests/CreateVisitRequestDetails";
import VisitRequestSuccess from "./layout/Contact_Person/VisitRequests/VisitRequestSuccess";
import ContactRestrictedManagement from "./layout/Contact_Person/BlacklistManagement/BlacklistManagement";

// Security Support Layouts
import Scanner from "./layout/Security_Officer/Scanner/Scanner";
import Verification from "./layout/Security_Officer/Verification/Verification";
import EntryApproval from "./layout/Security_Officer/EntryApproval/EntryApproval";
import ActiveVisitors from "./layout/Security_Officer/ActiveVisitors/ActiveVisitors";
import ExitVerification from "./layout/Security_Officer/ExitVerification/ExitVerification";
import IncidentReport from "./layout/Security_Officer/IncidentReport/IncidentReport";
import LogsHistory from "./layout/Security_Officer/LogsHistory/LogsHistory";
import SecurityNotifications from "./layout/Security_Officer/Notifications/Notifications";
import SecurityDashboard from "./layout/Security_Officer/Dashboard/Dashboard";
import SecurityRestrictedManagement from "./layout/Security_Officer/BlacklistManagement/BlacklistManagement";

import { useEffect, useMemo } from "react";
import { updateIsMobile } from "./reducers/uiSlice";
import themeColors from "./theme/themeColors";
import { ThemeModeProvider, useThemeMode } from "./theme/ThemeModeContext";

const paletteByMode = {
  dark: {
    backgroundDefault: themeColors.background.DEFAULT,
    backgroundPaper: themeColors.secondary.paper,
    textPrimary: themeColors.text.primary,
    textSecondary: themeColors.text.secondary,
    paperBorder: "rgba(255, 255, 255, 0.05)",
  },
  light: {
    backgroundDefault: "#F4F6F8",
    backgroundPaper: "#FFFFFF",
    textPrimary: "#1F2937",
    textSecondary: "#4B5563",
    paperBorder: "rgba(17, 24, 39, 0.1)",
  },
};

const createAppTheme = (mode) => {
  const palette = paletteByMode[mode] || paletteByMode.dark;

  return createTheme({
    palette: {
      mode,
      primary: {
        main: themeColors.primary.DEFAULT,
        dark: themeColors.primary.hover,
        light: "#D32F2F",
      },
      background: {
        default: palette.backgroundDefault,
        paper: palette.backgroundPaper,
      },
      text: {
        primary: palette.textPrimary,
        secondary: palette.textSecondary,
      },
    },
    typography: {
      fontFamily: '"Inter", "Plus Jakarta Sans", sans-serif',
      h1: { fontWeight: 800, letterSpacing: "-0.025em" },
      h2: { fontWeight: 700, letterSpacing: "-0.015em" },
      h3: { fontWeight: 700 },
      button: { textTransform: "none", fontWeight: 600, letterSpacing: "0.01em" },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            padding: "10px 24px",
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          },
          containedPrimary: {
            background: "linear-gradient(135deg, #C8102E, #A60D26)",
            boxShadow: "0 4px 14px rgba(200,16,46,0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #A60D26, #8B0C1F)",
              boxShadow: "0 6px 20px rgba(200,16,46,0.4)",
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: palette.backgroundPaper,
            border: `1px solid ${palette.paperBorder}`,
            borderRadius: 16,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 10,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 20 },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 16 },
        },
      },
    },
  });
};

const AppContent = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => dispatch(updateIsMobile());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch]);

  const isLoginPage = location.pathname === "/login";
  const isDashboardPath =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/contact_person") ||
    location.pathname.startsWith("/security-dashboard") ||
    location.pathname.startsWith("/Security_Officer");

  return (
    <>
      {!isLoginPage && !isDashboardPath && <HeaderComponent />}
      <Layout>
        <Routes>
          {/* Entry Route */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/server-config" element={<ServerConfig />} />

          {/* Visitor Routes */}
          <Route path="/home" element={<Home />} />
          <Route path="/request-step-1" element={<Step1Request />} />
          <Route path="/request-step-2" element={<Step2Request />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/qr" element={<QRPage />} />
          <Route path="/instructions" element={<InstructionsPage />} />
          <Route path="/visitor/my-requests" element={<MyRequests />} />
          <Route path="/visitor/request-details/:requestId" element={<RequestDetails />} />
          <Route path="/visitor/gate-pass/:gatePassId" element={<GatePass />} />
          <Route path="/visitor/profile-token" element={<ProfileToken />} />

          {/* Admin Routes */}
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route
            path="/admin/approval-management"
            element={<ApprovalManagement />}
          />
          <Route
            path="/admin/security-monitoring"
            element={<SecurityMonitoringPage />}
          />
          <Route
            path="/admin/blacklist-management"
            element={<RestrictedManagement />}
          />

          <Route path="/admin/user-management" element={<UserManagement />} />
          <Route path="/admin/inside-visitors" element={<AdminActiveVisitors defaultTab="inside" />} />
          <Route path="/admin/visitor-logs" element={<AdminActiveVisitors defaultTab="all" />} />
          <Route path="/admin/all-users" element={<AllUsers />} />
          <Route path="/admin/visitor-management" element={<VisitorManagement />} />
          <Route path="/admin/profile-tokens" element={<ProfileTokenManagement />} />
          <Route path="/admin/access-tokens" element={<AccessTokenManagement />} />
          <Route path="/admin/places-management" element={<PlacesManagement />} />
          <Route
            path="/admin-dashboard"
            element={<Navigate to="/admin/dashboard" replace />}
          />

          {/* Host Routes */}
          <Route
            path="/contact_person/dashboard"
            element={<ContactDashboard />}
          />
          <Route
            path="/contact_person/request-review"
            element={<RequestReview />}
          />
          <Route
            path="/contact_person/approved-requests"
            element={<Navigate to="/contact_person/visit-requests" replace />}
          />
          <Route
            path="/contact_person/rejected-requests"
            element={<DeclinedRequests />}
          />
          <Route
            path="/contact_person/sent-to-admin"
            element={<SentToAdmin />}
          />
          <Route
            path="/contact_person/visitor-history"
            element={<VisitorHistory />}
          />
          <Route
            path="/contact_person/profile-settings"
            element={<ProfileSettings />}
          />
          <Route
            path="/contact_person/all-visitors"
            element={<ContactAllVisitors />}
          />
          <Route
            path="/contact_person/visit-requests"
            element={<ContactVisitRequests />}
          />
          <Route
            path="/contact_person/create-visit-request"
            element={<CreateVisitRequest />}
          />
          <Route
            path="/contact_person/create-visit-request-details"
            element={<CreateVisitRequestDetails />}
          />
          <Route
            path="/contact_person/visit-request-success/:requestId"
            element={<VisitRequestSuccess />}
          />
          <Route
            path="/contact_person/blacklist-management"
            element={<ContactRestrictedManagement />}
          />

          {/* Security Support Routes */}
          <Route path="/Security_Officer/scanner" element={<Scanner />} />
          <Route
            path="/Security_Officer/verification"
            element={<Verification />}
          />
          <Route
            path="/Security_Officer/entry-approval"
            element={<EntryApproval />}
          />
          <Route
            path="/Security_Officer/active-visitors"
            element={<Navigate to="/Security_Officer/inside-visitors" replace />}
          />
          <Route
            path="/Security_Officer/inside-visitors"
            element={<ActiveVisitors defaultTab="inside" />}
          />
          <Route
            path="/Security_Officer/left-visitors"
            element={<ActiveVisitors defaultTab="left" />}
          />
          <Route
            path="/Security_Officer/exit-verification"
            element={<ExitVerification />}
          />
          <Route
            path="/Security_Officer/incident-report"
            element={<IncidentReport />}
          />
          <Route
            path="/Security_Officer/logs-history"
            element={<LogsHistory />}
          />
          <Route
            path="/Security_Officer/notifications"
            element={<SecurityNotifications />}
          />
          <Route
            path="/Security_Officer/dashboard"
            element={<SecurityDashboard />}
          />
          <Route
            path="/Security_Officer/blacklist-management"
            element={<SecurityRestrictedManagement />}
          />

          <Route
            path="/security-dashboard"
            element={<Navigate to="/Security_Officer/dashboard" replace />}
          />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Layout>
    </>
  );
};

function App() {
  return (
    <Provider store={store}>
      <ThemeModeProvider>
        <AppShell />
      </ThemeModeProvider>
    </Provider>
  );
}

const AppShell = () => {
  const { themeMode } = useThemeMode();

  const theme = useMemo(() => createAppTheme(themeMode), [themeMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
};

export default App;
